/**
 * ChatMessage Component
 * Renders individual chat messages using AG-UI Message type
 * Supports markdown rendering for AI responses and DGUI (multipart) content
 */

import type { ReactNode } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { Person as PersonIcon, SmartToy as AIIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message } from '../types/agui';
import {
  isDGUIMessageContent,
  type ContentPart,
  type DGUIComponentSchema,
} from '../types/dgui';

interface ChatMessageProps {
  message: Message;
  isStreaming?: boolean;
  /** Optional renderer for DGUI blocks (e.g. from web app registry). */
  renderDgui?: (schema: DGUIComponentSchema) => ReactNode;
  /** Optional custom icon or image for the assistant avatar. When provided, used instead of the default icon. */
  assistantAvatar?: ReactNode;
}

function renderContentPart(
  part: ContentPart,
  isUser: boolean,
  renderDgui?: (schema: DGUIComponentSchema) => ReactNode,
  markdownSx?: object
): ReactNode {
  if (part.type === 'text') {
    const text = part.text || '';
    if (isUser) {
      return (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {text}
        </Typography>
      );
    }
    return (
      <Box className="markdown-body" sx={markdownSx}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
      </Box>
    );
  }
  if (part.type === 'dgui' && part.schema && renderDgui) {
    return renderDgui(part.schema);
  }
  return null;
}

export function ChatMessage({ message, isStreaming, renderDgui, assistantAvatar }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const rawContent = message.content;

  /** Plain string or legacy: single text block. */
  let contentNode: ReactNode;
  if (typeof rawContent === 'string') {
    if (isUser) {
      contentNode = (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {rawContent}
        </Typography>
      );
    } else {
      contentNode = (
        <Box className="markdown-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{rawContent}</ReactMarkdown>
        </Box>
      );
    }
  } else if (isDGUIMessageContent(rawContent) && rawContent.parts.length > 0) {
    /** Structured multipart (text + DGUI). */
    const markdownSx = {};
    contentNode = (
      <>
        {rawContent.parts.map((part, i) => (
          <Box key={i} sx={{ mb: part.type === 'dgui' ? 0 : 0 }}>
            {renderContentPart(part, isUser, renderDgui, markdownSx)}
          </Box>
        ))}
      </>
    );
  } else {
    /** Fallback: object content stringified. */
    const content = typeof rawContent === 'string'
      ? rawContent
      : JSON.stringify(rawContent ?? '');
    if (isUser) {
      contentNode = (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {content}
        </Typography>
      );
    } else {
      contentNode = (
        <Box className="markdown-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
        </Box>
      );
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          maxWidth: '75%',
          flexDirection: isUser ? 'row-reverse' : 'row',
        }}
      >
        <Avatar sx={{ bgcolor: isUser ? 'primary.main' : '#fff8ef' }}>
          {isUser ? <PersonIcon /> : (assistantAvatar ?? <AIIcon />)}
        </Avatar>
        <Box
          sx={{
            bgcolor: isUser ? 'primary.light' : 'grey.100',
            p: 1.5,
            borderRadius: 2,
            '& .markdown-body': {
              // Markdown styling
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              color: 'text.primary',
              '& p': {
                margin: '0 0 0.75rem 0',
                '&:last-child': {
                  marginBottom: 0,
                },
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: '1rem',
                marginBottom: '0.5rem',
                fontWeight: 600,
                lineHeight: 1.25,
                color: '#04377b',
                '&:first-child': {
                  marginTop: 0,
                },
              },
              '& h1': { fontSize: '1.5rem' },
              '& h2': { fontSize: '1.25rem' },
              '& h3': { fontSize: '1.125rem' },
              '& h4': { fontSize: '1rem' },
              '& ul, & ol': {
                margin: '0.5rem 0',
                paddingLeft: '1.5rem',
              },
              '& li': {
                margin: '0.25rem 0',
              },
              '& code': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                padding: '0.125rem 0.25rem',
                borderRadius: '3px',
                fontSize: '0.875em',
                fontFamily: 'monospace',
                color: '#04377b',
              },
              '& pre': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                padding: '0.75rem',
                borderRadius: '4px',
                overflow: 'auto',
                margin: '0.75rem 0',
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                },
              },
              '& blockquote': {
                borderLeft: '3px solid rgba(4, 55, 123, 0.3)',
                paddingLeft: '1rem',
                margin: '0.75rem 0',
                color: 'text.secondary',
                fontStyle: 'italic',
              },
              '& table': {
                borderCollapse: 'collapse',
                margin: '0.75rem 0',
                width: '100%',
                '& th, & td': {
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  padding: '0.5rem',
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: 'rgba(4, 55, 123, 0.1)',
                  fontWeight: 600,
                },
              },
              '& a': {
                color: '#04377b',
                textDecoration: 'underline',
                '&:hover': {
                  color: '#032e64',
                },
              },
              '& strong': {
                fontWeight: 600,
              },
              '& em': {
                fontStyle: 'italic',
              },
              '& hr': {
                border: 'none',
                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                margin: '1rem 0',
              },
            },
          }}
        >
          {contentNode}
          {isStreaming && (
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 8,
                height: 8,
                bgcolor: 'primary.main',
                borderRadius: '50%',
                ml: 1,
                animation: 'pulse 1s infinite',
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
